import { useState } from "react";
import {
  Alert,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import {
  CredentialAccountSpec,
  CredentialFieldSpec,
  ProviderCredentialSchema,
} from "../utils/api";

type Props = {
  /** Схема провайдера. undefined — провайдер не выбран или схемы нет. */
  schema?: ProviderCredentialSchema;
  /** Схемы ещё грузятся: показываем прежний ввод, а не пустоту. */
  loading?: boolean;
  accountCode: string;
  onAccountChange: (code: string) => void;
  values: Record<string, string>;
  onValueChange: (name: string, value: string) => void;
  /** При правке пустые поля означают «ключи не трогаем». */
  editing: boolean;
  /** Прежний ввод «ИМЯ = значение» — когда поля провайдера не описаны. */
  manual: string;
  onManualChange: (text: string) => void;
};

/** Поле без переменной окружения работает только через набор. */
const onlyViaSet = (field: CredentialFieldSpec) => field.env === null;

const FieldInput = ({
  field,
  value,
  onChange,
  editing,
}: {
  field: CredentialFieldSpec;
  value: string;
  onChange: (value: string) => void;
  editing: boolean;
}) => {
  const [shown, setShown] = useState(false);

  const hint = [
    field.hint,
    field.env ? `Раньше бралось из ${field.env}` : null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <TextField
      label={field.title}
      size="small"
      fullWidth
      // При правке обязательность не держим: пустая форма означает
      // «ключи не трогаем», и звёздочка у всех полей сбивала бы с толку.
      required={field.required && !editing}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      type={field.secret && !shown ? "password" : "text"}
      placeholder={field.default ?? undefined}
      helperText={hint || undefined}
      InputProps={{
        // Значение переносят из кабинета провайдера — без возможности
        // посмотреть опечатку в маске не поймать.
        endAdornment: field.secret ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              aria-label={shown ? "Скрыть значение" : "Показать значение"}
              onClick={() => setShown((prev) => !prev)}
              edge="end"
            >
              {shown ? (
                <VisibilityOffOutlined fontSize="small" />
              ) : (
                <VisibilityOutlined fontSize="small" />
              )}
            </IconButton>
          </InputAdornment>
        ) : undefined,
      }}
    />
  );
};

/**
 * Ключи набора: форма строится по ответу /provider_credentials/fields.
 *
 * Имена полей угадать нельзя — у fastpay переменная окружения называется
 * FASTPAY_SECRET, а поле набора SECRET_KEY, — и ошибка в имени тихая: набор
 * выглядит настроенным, а платежи идут по прежним ключам из окружения.
 * Поэтому имена даёт бэкенд, а человек вводит только значения.
 */
const CredentialPayloadForm = ({
  schema,
  loading,
  accountCode,
  onAccountChange,
  values,
  onValueChange,
  editing,
  manual,
  onManualChange,
}: Props) => {
  const manualInput = (
    <TextField
      label={editing ? "Новые ключи" : "Ключи набора"}
      size="small"
      fullWidth
      multiline
      minRows={4}
      value={manual}
      onChange={(event) => onManualChange(event.target.value)}
      helperText={
        editing
          ? "Пусто — ключи не меняются. Заполнено — набор заменяется целиком."
          : "По строке на поле: ИМЯ_ПОЛЯ = значение"
      }
    />
  );

  if (loading) {
    return (
      <Typography variant="caption" color="text.secondary">
        Загружаем поля провайдера…
      </Typography>
    );
  }

  // Провайдер не выбран или схем нет вовсе (старый бэкенд) — остаётся
  // прежний ввод имён вручную.
  if (!schema) return manualInput;

  if (!schema.reads_sets) {
    return (
      <Alert severity="warning" sx={{ borderRadius: "12px" }}>
        {schema.note ??
          "Эта интеграция читает ключи только из окружения — набор можно завести, но он ни на что не повлияет."}
      </Alert>
    );
  }

  // Провайдер описан не полностью: имена вводятся вручную.
  if (!schema.accounts.length) {
    return (
      <Stack spacing={1.5}>
        {schema.note && (
          <Alert severity="info" sx={{ borderRadius: "12px" }}>
            {schema.note}
          </Alert>
        )}
        {manualInput}
      </Stack>
    );
  }

  const account: CredentialAccountSpec | undefined =
    schema.accounts.find((item) => item.code === accountCode) ??
    (schema.accounts.length === 1 ? schema.accounts[0] : undefined);

  return (
    <Stack spacing={1.75}>
      {schema.note && (
        <Alert severity="info" sx={{ borderRadius: "12px" }}>
          {schema.note}
        </Alert>
      )}

      {schema.accounts.length > 1 && (
        <Select
          size="small"
          fullWidth
          displayEmpty
          value={account?.code ?? ""}
          onChange={(event) => onAccountChange(event.target.value)}
        >
          <MenuItem value="">Счёт провайдера</MenuItem>
          {schema.accounts.map((item) => (
            <MenuItem key={item.code} value={item.code}>
              {item.title}
            </MenuItem>
          ))}
        </Select>
      )}

      {/* BASE_URL и прочее вне счёта: уходит в тот же набор, что и ключи. */}
      {schema.common_fields.map((field) => (
        <FieldInput
          key={field.name}
          field={field}
          value={values[field.name] ?? ""}
          onChange={(value) => onValueChange(field.name, value)}
          editing={editing}
        />
      ))}

      {!account ? (
        <Typography variant="caption" color="text.secondary">
          Выберите счёт — под него и заводятся ключи.
        </Typography>
      ) : (
        <Stack spacing={1.75}>
          {account.note && (
            <Typography variant="caption" color="text.secondary">
              {account.note}
            </Typography>
          )}

          {account.fields.map((field) => (
            <Stack key={field.name} spacing={0.5}>
              <FieldInput
                field={field}
                value={values[field.name] ?? ""}
                onChange={(value) => onValueChange(field.name, value)}
                editing={editing}
              />
              {onlyViaSet(field) && (
                <Tooltip title="У этого поля нет переменной окружения: не заполнив его, метод не запустить">
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label="только через набор"
                    sx={{ height: 20, fontSize: 11, alignSelf: "flex-start" }}
                  />
                </Tooltip>
              )}
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export default CredentialPayloadForm;
